import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { API } from "@service/system/api.service";
import { HttpManager } from "@service/system/httpManager.service";
import {
	ProductLine,
	ProductLineResponse,
	OrganizationProductLineRelationship,
	ProductLineMetaData,
	ProductLineRelationship,
	UnaffiliatedProduct,
	UnaffiliatedOrganization,
	ProductLineSunsetContentRecord
} from "@data/productLine";
import { ServiceResponse } from "@data/response";
import { ResponseHandler } from "@service/system/responseHandler.service";

const API_URL = API.API_URL;

@Injectable()
export class ProductLineService {
	private _productLineURL = `${API_URL}/productlines`;

	constructor(private httpManager: HttpManager, private responseHandler: ResponseHandler) {}

	/* -- Product Lines -- */
	getProductLines(
		status: string,
		perpetual: string = null,
		temporal: string = null
	): Observable<ServiceResponse<ProductLine[]>> {
		return this.responseHandler.httpGet<ProductLine[]>(`${this._productLineURL}`, {
			status: status != null ? status : "true",
			perpetual: perpetual != null ? perpetual : null,
			temporal: temporal != null ? temporal : null
		});
	}

	getProductLinesByString(searchString: string): Observable<ServiceResponse<ProductLine[]>> {
		searchString = searchString == null ? "" : searchString;
		return this.responseHandler.httpGet<ProductLine[]>(`${this._productLineURL}/find`, {
			searchString: searchString
		});
	}

	setProductLineMetaData(plItemID: string, body: string): Observable<ServiceResponse<ProductLineResponse[]>> {
		return this.responseHandler.httpPost<ProductLineResponse[]>(`${this._productLineURL}/${plItemID}/update`, body);
	}

	uploadHeaderGraphic(plItemID: string, body: FormData): Observable<ServiceResponse<ProductLineResponse[]>> {
		return this.httpManager.httpPostMediaFile<any>(`${this._productLineURL}/${plItemID}/uploadHeaderImage`, body);
	}

	/* -- Product Lines - Organizations -- */

	getProductLineParticipatingOrganizationsByString(
		plItemID: string
	): Observable<ServiceResponse<OrganizationProductLineRelationship[]>> {
		return this.responseHandler.httpGet<OrganizationProductLineRelationship[]>(
			`${this._productLineURL}/${plItemID}/orgs`
		);
	}

	setOrganizationRelationships(plItemID: string, body: string): Observable<ServiceResponse<ProductLineResponse[]>> {
		return this.responseHandler.httpPost<ProductLineResponse[]>(`${this._productLineURL}/${plItemID}/orgs`, body);
	}

	getUnaffiliatedOrganizationsByProductLine(
		plItemID: string
	): Observable<ServiceResponse<UnaffiliatedOrganization[]>> {
		return this.responseHandler.httpGet<UnaffiliatedOrganization[]>(
			`${this._productLineURL}/${plItemID}/orgs/unaffiliated`
		);
	}

	/* -- Product Lines - Products -- */

	getProductLineRelationshipsByString(searchString: string): Observable<ServiceResponse<ProductLineRelationship[]>> {
		searchString = searchString == null ? "" : searchString;
		return this.responseHandler.httpGet<ProductLineRelationship[]>(`${this._productLineURL}/products`, {
			searchString: searchString
		});
	}
	setProductRelationships(plItemID: string, body: string): Observable<ServiceResponse<ProductLineResponse[]>> {
		return this.responseHandler.httpPost<ProductLineResponse[]>(
			`${this._productLineURL}/${plItemID}/products`,
			body
		);
	}

	getProductMetaDataByProductLine(plItemID: string): Observable<ServiceResponse<ProductLineMetaData[]>> {
		return this.responseHandler.httpGet<ProductLineMetaData[]>(`${this._productLineURL}/${plItemID}/metadata`);
	}
	getUnaffiliatedProductsByProductLine(
		plItemID: string,
		status: string
	): Observable<ServiceResponse<UnaffiliatedProduct[]>> {
		return this.responseHandler.httpGet<UnaffiliatedProduct[]>(
			`${this._productLineURL}/${plItemID}/products/unaffiliated`,
			{
				status: status
			}
		);
	}

	/* -- Product Lines - Sunsetting Content -- */
	getProductLineSunsetContentRecords(
		plItemID: string
	): Observable<ServiceResponse<ProductLineSunsetContentRecord[]>> {
		return this.responseHandler.httpGet<ProductLineSunsetContentRecord[]>(
			`${this._productLineURL}/${plItemID}/sunsetcontent`
		);
	}

	deleteProductLineSunsetContentRecords(plItemID: string): Observable<ServiceResponse<ProductLineResponse[]>> {
		return this.responseHandler.httpDelete<ProductLineResponse[]>(
			`${this._productLineURL}/${plItemID}/sunsetcontent/delete?confirmation=true`
		);
	}
}
